import { useEffect, useMemo, useRef, useState, type SubmitEvent } from "react";
import { Element, useEditor } from "@nkitaa/builder-engine";
import { submitDataInput } from "../../lib/runtimePage";
import { fetchBookingPage, submitBooking, submitRequest } from "../../lib/slottingApi";
import {
  parseCapacityRefusal,
  parseOptionCapacityFullError,
  parseResubmitBlockedError,
  parseSessionFullError,
  parseOfferingConflictError,
  parseOfferingNotBookableError,
  parseOfferingMultipleAppointmentsError,
} from "../../lib/sessionsApi";
import { ApiError } from "../../lib/api";
import { useWBTheme, useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import {
  validateSubmissionClientSide,
  type AdditionalFieldSchema,
  type AdditionalFieldViolation,
} from "../../lib/additionalFieldConstraints";
import { selectAdditionalFieldValues } from "../../lib/dataInputSplit";
import { isStoredImageValue } from "../lib/image-upload";
import type { Field } from "../types/core";
import {
  type BorderSide,
  type CollectedFieldValues,
  type RuntimeProps,
  type SubmitState,
  WB_VALIDATE_EVENT,
  WB_BOOKING_ERROR_EVENT,
  WB_BOOKING_SUMMARY_EVENT,
  WB_SESSION_FULL_EVENT,
  WB_OFFERING_CONFLICT_EVENT,
  WB_SUBMIT_SUCCESS_EVENT,
  readCachedEvent,
  DataInputDraftContext,
  DataInputNavigationContext,
  DataInputReadOnlyContext,
  DataInputVerificationContext,
  StatusMessage,
  ThemeSectionContainer,
  classNames,
  collectFieldValues,
  extractUtilityClasses,
  formatOverviewValue,
  isPlainRecord,
  isVisible,
  mapOverviewLabelToVerification,
  parseContentAlignmentClass,
  sanitizeHtml,
  SubmitStateContext,
} from "./shared";
import {
  ACCEPTANCE_SUBMIT_VARIABLE,
  canonicalFieldVariable,
} from "./acceptanceField";
import { type WBReveal } from "./reveal";
import { t } from "../locale";
import { submitErrorText } from "../lib/submit-error";
import { Button } from "./button";
import {
  DataInputAcceptanceField,
  resolveDataInputSubmittedFlag,
  resolveSessionPrefillValue,
  resolveCompanionSessionPrefillValue,
} from "./formInput";
import {
  type BookingSummary,
  Checkboxes,
  CompanionStateBadge,
  CompanionsGroup,
  FieldsGroup,
} from "./formGroups";
import { BookingGroup } from "./bookingGroup";
import {
  SessionGroup,
  collectSessionsPayload,
  collectOfferingBookingsPayload,
  hasOfferingSelectionChanges,
  hasSessionSelectionChanges,
} from "./sessionGroup";
import { SectionHeader } from "./sectionHeader";

/**
 * The edit-mode "nothing changed, don't submit" gate.
 *
 * Extracted as a pure function because it is a decision with real
 * consequences and it was previously untestable — there is no test file for
 * this component, so a wrong answer here silently no-ops a guest's save.
 *
 * Both selection flags are ESCAPE HATCHES, not inputs to the comparison: a
 * guest who changes only a slot, or only a session pick, has byte-identical
 * field values, so comparing values alone would swallow the edit and show a
 * green "no changes" toast.
 */
export function shouldSkipUnchangedSubmit(args: {
  allowUpdate: boolean;
  isEditing: boolean;
  sessionSnapshot:
    | { values: Record<string, unknown>; companions: Record<string, unknown>[] }
    | null
    | undefined;
  values: Record<string, unknown>;
  companions: Record<string, unknown>[];
  offeringChanged: boolean;
  sessionChanged: boolean;
}): boolean {
  const {
    allowUpdate,
    isEditing,
    sessionSnapshot,
    values,
    companions,
    offeringChanged,
    sessionChanged,
  } = args;
  if (!allowUpdate || !isEditing || !sessionSnapshot) return false;
  if (offeringChanged || sessionChanged) return false;

  const valuesChanged = Object.keys(values).some((key) => {
    const prev = sessionSnapshot.values[key];
    const next = values[key];
    return String(prev ?? "") !== String(next ?? "");
  });
  if (valuesChanged) return false;

  const companionsChanged =
    companions.length !== sessionSnapshot.companions.length ||
    companions.some((c, i) => {
      const prev = sessionSnapshot.companions[i];
      if (!prev) return true;
      return Object.keys(c).some(
        (key) => String(c[key] ?? "") !== String(prev[key] ?? ""),
      );
    });
  return !companionsChanged;
}

/**
 * Whether the submit should carry the `sessions` key at all.
 *
 * On an UPDATE the guest never touched the picker with, the key is OMITTED
 * rather than re-asserting a set we only inferred from the seed. Costs
 * nothing (the worker leaves untouched picks alone) and shrinks the window
 * where the seed races a concurrent admin change down to "the guest
 * actually touched the picker".
 *
 * A FIRST submit always sends it — there, an empty selection is a
 * legitimate answer, not an absence of intent.
 *
 * Pure and exported so this decision is testable; it was previously inline
 * and had no coverage at all.
 */
export function shouldSendSessions(args: {
  sessionsPayload: unknown;
  allowUpdate: boolean;
  isEditing: boolean;
  sessionChanged: boolean;
}): boolean {
  const { sessionsPayload, allowUpdate, isEditing, sessionChanged } = args;
  if (sessionsPayload === undefined) return false;
  if (allowUpdate && isEditing && !sessionChanged) return false;
  return true;
}

const DI_SUBMITTED_KEY_PREFIX = "veltric-di-submitted-";

/**
 * Convert the runtime `Field[]` shape (from WBThemeRuntimeProvider, which
 * forwards the bootstrap response's fields[]) into the AdditionalFieldSchema
 * shape the shared validator consumes. Numeric bounds become string for
 * STRING/LONG/DOUBLE length + numeric comparisons; Date bounds become ISO
 * strings so Date.parse recovers them in checkValue.
 */
function fieldsToSchema(fields: Field[] | undefined): AdditionalFieldSchema[] {
  if (!fields || fields.length === 0) return [];
  // Synthetic built-in condition entries (additionalField === false) carry only
  // conditions, not a real additional-field schema — they must not enter the
  // additional-field client validator (they'd be treated as unknown extras).
  return fields
    .filter((f) => f.additionalField)
    .map((f) => ({
    name: f.name,
    dataType: f.dataType as AdditionalFieldSchema["dataType"],
    required: f.required ?? null,
    options: f.options ?? null,
    lowerBound:
      f.lowerBound == null
        ? null
        : f.lowerBound instanceof Date
          ? f.lowerBound.toISOString()
          : String(f.lowerBound),
    upperBound:
      f.upperBound == null
        ? null
        : f.upperBound instanceof Date
          ? f.upperBound.toISOString()
          : String(f.upperBound),
  }));
}

/** Step index of the wrapper a (hidden) errored field sits in, or -1 when it
 *  has no step wrapper at all — the participation question renders in its own
 *  div, and a companion field lives in a portaled modal. */
function stepIndexOf(node: Element): number {
  const raw = node.closest("[data-wb-step]")?.getAttribute("data-wb-step");
  return raw === null || raw === undefined ? -1 : Number(raw);
}

/**
 * What a SUBMIT-time validation pass should do.
 *
 * `errorCount` counts every failing field; `hiddenErrorSteps` carries one entry
 * per error that was reported from a field the visitor cannot currently see
 * (its step index, or -1 when it has no step to jump to).
 *
 *  - errors the visitor CAN see → block; the inline message is on screen.
 *  - hidden errors we can navigate to → block AND jump to that step; this is
 *    the whole point of validating skipped steps.
 *  - hidden errors with nowhere to jump → PASS. A blocked submit with no
 *    message anywhere is a dead button, and this state is reachable: the
 *    participation question is always `required` and lives outside the step
 *    wrappers, gated on `fields-0` — a form whose steps no longer include that
 *    section can never show it. Letting the POST go leaves the server as the
 *    guard, exactly as before hidden fields were validated at all.
 */
export function decideValidationOutcome(
  errorCount: number,
  hiddenErrorSteps: number[],
): { pass: boolean; jumpToStep?: number } {
  if (errorCount === 0) return { pass: true };
  if (errorCount > hiddenErrorSteps.length) return { pass: false };
  const reachable = hiddenErrorSteps.find((step) => step >= 0);
  if (reachable !== undefined) return { pass: false, jumpToStep: reachable };
  return { pass: true };
}

/**
 * The field variables this form actually RENDERS, read off the DOM.
 *
 * Not the same as "the keys the submission carries": an untouched IMAGE widget
 * deliberately omits its key (`collectFieldValues`, shared.tsx — absent means
 * "keep the stored image"), so scoping by submitted keys alone would silently
 * drop required-IMAGE from the check.
 */
export function renderedFieldVariables(form: HTMLFormElement | null): Set<string> {
  const out = new Set<string>();
  if (!form) return out;
  form
    // Companion-owned nodes are EXCLUDED. `CompanionsGroup`'s summary card
    // renders a hidden bridge input per saved companion value, each carrying
    // `data-field-variable` — so a field placed ONLY in the companion group
    // (say a required "Allergies") landed in the MAIN guest's required scope.
    // The main form has no such input, so validation demanded a field the
    // visitor could never fill and the submit button died permanently; the
    // only escape was deleting the companion. The server would have accepted
    // the submission — this block was purely self-inflicted.
    //
    // NEGATIVE match on purpose: `CheckboxGroup` (formGroups.tsx:1638) emits
    // `data-field-variable` with NO `data-companion` attribute at all and is a
    // legitimate main-form field, so a positive `[data-companion="false"]`
    // filter would silently drop it back out of scope.
    //
    // `:not([disabled])` mirrors the SERVER, which subtracts
    // condition-suppressed fields from its required check
    // (additional-field-validator.ts `schemaForRequiredCheck`). A field whose
    // `editableCondition` the visitor does not meet renders disabled
    // (formInput.tsx:1669) but still carries `data-field-variable`, and
    // `collectFieldValues` reads `""` off it — so without this it was in scope
    // twice over, and a REQUIRED such field made the form permanently
    // unsubmittable: the banner named a field the visitor cannot type into,
    // every retry identical, while the server would have accepted the
    // submission.
    .querySelectorAll<HTMLElement>(
      '[data-field-variable]:not([data-companion="true"]):not([disabled])',
    )
    .forEach((el) => {
      const name = el.dataset.fieldVariable;
      if (name) out.add(name.toLowerCase());
    });
  return out;
}

/**
 * Restrict the schema to the fields this page can actually collect.
 *
 * The bootstrap ships every additional field defined on the event — not the
 * ones the builder placed on this page. The required check walks the schema,
 * so a required field the form does not render (never placed, dropped by the
 * injector for an unsupported data type, or unmounted by a visibility
 * condition) produced `requiredAdditionalFieldMissing` for a field the visitor
 * cannot see, and `onSubmit` returned before any POST — permanently
 * unsubmittable, with no retry that could ever work.
 *
 * Scope = rendered variables ∪ submitted keys. The union matters at both ends:
 * rendered-but-key-less fields (IMAGE) keep their required check, and companion
 * records — whose inputs live in a portaled modal that is closed at submit time,
 * so they are NOT in the form DOM — are scoped by their own keys.
 *
 * ⚠ The server must agree, or this only moves the failure: `/data-input/submit`
 * used to required-check the FULL event schema (website-builder-backend
 * `routes/runtime.ts`, `listByEvent` → `schemaForRequiredCheck`), which would
 * turn a client pass into a bare "Request failed with status 400". It now
 * filters by the submitted keys exactly like `/auth/register` does.
 */
export function schemaForSubmitted(
  schema: AdditionalFieldSchema[],
  submitted: Record<string, unknown>,
  rendered: Set<string> = new Set(),
): AdditionalFieldSchema[] {
  const present = new Set(Object.keys(submitted).map((k) => k.toLowerCase()));
  return schema.filter(
    (f) =>
      present.has(f.name.toLowerCase()) || rendered.has(f.name.toLowerCase()),
  );
}

/** Everything the action row's visibility depends on. */
export type ActionRowState = {
  multiStepEnabled: boolean;
  multiStepMode: "pages" | "single";
  /** number of FIELD steps (`stepSections.length`) */
  totalSteps: number;
  /** a PRE-submit review step exists — it sits at index `totalSteps` */
  hasReviewStep: boolean;
  currentStep: number;
  /** post-submit read-only summary of a non-updatable form */
  isReadOnlyVerification: boolean;
  /** post-submit summary of an updatable form (has its own Update button) */
  isAllowUpdateOverview: boolean;
};

export type ActionRowVisibility = {
  back: boolean;
  next: boolean;
  submit: boolean;
  /** always tracks `submit` — consent has to be reachable where you confirm */
  checkboxes: boolean;
};

/**
 * WHICH action button each step shows — the one contract the editor twin
 * (main-frontend sections/DataInput/DataInput.tsx) and this runtime must agree
 * on, kept honest by an identical truth table on both sides.
 *
 * The rule: submit + checkboxes appear on the LAST effective step only — the
 * review step when the form has one, otherwise the last field step — and never
 * on either post-submit screen.
 */
export function actionRowVisibility(s: ActionRowState): ActionRowVisibility {
  const pages = s.multiStepEnabled && s.multiStepMode === "pages";
  const effectiveTotal = Math.max(
    1,
    pages && s.hasReviewStep ? s.totalSteps + 1 : s.totalSteps,
  );
  const postSubmit = s.isReadOnlyVerification || s.isAllowUpdateOverview;
  const submit = !postSubmit && (!pages || s.currentStep === effectiveTotal - 1);
  return {
    submit,
    checkboxes: submit,
    back: pages && !postSubmit && s.currentStep > 0,
    next: pages && !postSubmit && s.currentStep < effectiveTotal - 1,
  };
}

/**
 * Client mirror of the worker's required-IMAGE fix: an untouched image
 * widget submits NOTHING (collectFieldValues omits the key), but the
 * guest's STORED image still satisfies `required` — so the client-side
 * required-missing violation is dropped when a stored value exists and
 * the submission left the field untouched (key absent). An explicit
 * clear (`null` in the submitted map) keeps the violation, exactly like
 * blanking any other required field.
 */
function dropSatisfiedImageRequired(
  violations: AdditionalFieldViolation[],
  schema: readonly AdditionalFieldSchema[],
  submitted: Record<string, unknown>,
  storedLookup: (fieldName: string) => unknown,
): AdditionalFieldViolation[] {
  if (violations.length === 0) return violations;
  return violations.filter((v) => {
    if (v.code !== "requiredAdditionalFieldMissing") return true;
    const lower = v.fieldKey.toLowerCase();
    const field = schema.find((f) => f.name.toLowerCase() === lower);
    // fieldsToSchema passes the runtime dataType through unchanged; the
    // shared validator's union predates IMAGE, hence the String compare.
    if (!field || String(field.dataType) !== "IMAGE") return true;
    const submittedKey = Object.keys(submitted).find(
      (k) => k.toLowerCase() === lower,
    );
    // Explicit clear (null) — keep the violation. Untouched = absent/"".
    if (submittedKey !== undefined && submitted[submittedKey] !== "")
      return true;
    return !isStoredImageValue(storedLookup(field.name));
  });
}

function formatViolationsForDisplay(
  violations: readonly AdditionalFieldViolation[],
): string {
  return violations
    .map((v) => `${v.fieldKey}: ${t(("errors." + v.code) as Parameters<typeof t>[0])}`)
    .join(" · ");
}

function markDataInputSubmitted(session: unknown) {
  try {
    const userId = resolveSessionUserId(session);
    if (userId)
      localStorage.setItem(`${DI_SUBMITTED_KEY_PREFIX}${userId}`, "1");
  } catch {
    /* SSR / restricted storage */
  }
}

function isDataInputSubmittedLocally(session: unknown): boolean {
  try {
    const userId = resolveSessionUserId(session);
    if (userId)
      return (
        localStorage.getItem(`${DI_SUBMITTED_KEY_PREFIX}${userId}`) === "1"
      );
  } catch {
    /* SSR / restricted storage */
  }
  return false;
}

function resolveSessionUserId(session: unknown): string | undefined {
  if (!session || typeof session !== "object") return undefined;
  const s = session as Record<string, unknown>;
  const user = s.user;
  if (!user || typeof user !== "object") return undefined;
  const u = user as Record<string, unknown>;
  return (u.userId ?? u.inviteeId ?? u.guestId) as string | undefined;
}

/** Fields that are always included in the companion snapshot for the overview,
 *  even when they are not defined as form fields in the builder. */
const COMPANION_META_FIELDS = [
  "state",
  "acceptanceState",
  "firstName",
  "lastName",
];

/** Extract inviteeId for each companion from the session so that existing
 *  companions are updated (not re-created) when the form is resubmitted. */
function getSessionCompanionIds(session: unknown): string[] {
  if (!session || typeof session !== "object") return [];
  const s = session as Record<string, unknown>;
  const user = s.user;
  const companions =
    (user && typeof user === "object"
      ? (user as Record<string, unknown>).companions
      : undefined) ?? s.companions;
  if (!Array.isArray(companions)) return [];
  return companions.map((c: unknown) => {
    if (!c || typeof c !== "object") return "";
    const rec = c as Record<string, unknown>;
    return String(rec.inviteeId ?? rec.userId ?? rec.guestId ?? "");
  });
}

function buildSessionSnapshot(
  session: unknown,
  fieldVariables: string[],
  companionCount: number,
  companionVariables: string[],
  checkboxItems: Array<{
    text: string;
    required: boolean;
    inputFieldVariable?: string;
  }>,
): CollectedFieldValues | null {
  const values: Record<string, unknown> = {};
  for (const variable of fieldVariables) {
    const v = resolveSessionPrefillValue(session, variable);
    if (v !== undefined) values[variable] = v;
  }

  // Merge declared companion variables with always-included meta fields
  const allCompanionVars = Array.from(
    new Set([...companionVariables, ...COMPANION_META_FIELDS]),
  );

  const companions: Array<Record<string, unknown>> = [];
  for (let i = 0; i < companionCount; i++) {
    const companion: Record<string, unknown> = {};
    let hasValue = false;
    for (const variable of allCompanionVars) {
      const v = resolveCompanionSessionPrefillValue(session, variable, i);
      if (v !== undefined) {
        companion[variable] = v;
        hasValue = true;
      }
    }
    if (hasValue) companions.push(companion);
  }

  // Populate checkbox states from session data
  const checkboxes = checkboxItems.map((item) => {
    let checked = false;
    if (item.inputFieldVariable && session && typeof session === "object") {
      const s = session as Record<string, unknown>;
      const user = s.user;
      const userRec =
        user && typeof user === "object"
          ? (user as Record<string, unknown>)
          : undefined;
      const userAdditional =
        userRec?.additionalFields && typeof userRec.additionalFields === "object"
          ? (userRec.additionalFields as Record<string, unknown>)
          : undefined;
      const val =
        userRec?.[item.inputFieldVariable] ??
        userAdditional?.[item.inputFieldVariable] ??
        (s.additionalFields && typeof s.additionalFields === "object"
          ? (s.additionalFields as Record<string, unknown>)[
              item.inputFieldVariable
            ]
          : undefined);
      if (typeof val === "boolean") {
        checked = val;
      } else if (val === "true" || val === "1") {
        checked = true;
      }
    }
    const base = { text: item.text, required: item.required, checked };
    return item.inputFieldVariable
      ? { ...base, inputFieldVariable: item.inputFieldVariable }
      : base;
  });

  if (
    Object.keys(values).length === 0 &&
    companions.length === 0 &&
    checkboxes.length === 0
  )
    return null;
  return { values, companions, checkboxes };
}

/** Normalised booking display data used by BookingSummaryCard. */
type BookingDisplayAppointment = { treatmentName?: string; dateTime: string; status?: string };
type BookingDisplayRequest = { treatmentNames: string[]; windows: { dateStr: string; timeRange: string }[] };
type BookingDisplayData =
  | { mode: "APPOINTMENT"; appointment: BookingDisplayAppointment; request?: BookingDisplayRequest }
  | { mode: "REQUEST"; request: BookingDisplayRequest };

/** Single card for one offering's booking display data */
function SingleBookingSummaryCard({
  rounding,
  roundedClasses,
  cardBg,
  cardBorder,
  labelColor,
  valueColor,
  offeringId,
}: {
  rounding: string;
  roundedClasses: string;
  cardBg: string;
  cardBorder: string;
  labelColor: string;
  valueColor: string;
  offeringId: string;
}) {
  const [display, setDisplay] = useState<BookingDisplayData | null>(() => {
    const cached = readCachedEvent<BookingSummary>(`${WB_BOOKING_SUMMARY_EVENT}:${offeringId}`);
    return cached ? summaryToDisplay(cached) : null;
  });
  const loadedFromBackend = useRef(false);
  const eventTz = useEventTimezone();

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as BookingSummary | null;
      if (detail) {
        loadedFromBackend.current = false;
        setDisplay(summaryToDisplay(detail));
      } else if (!loadedFromBackend.current) {
        setDisplay(null);
      }
    };
    const eventKey = `${WB_BOOKING_SUMMARY_EVENT}:${offeringId}`;
    window.addEventListener(eventKey, handler);
    return () => window.removeEventListener(eventKey, handler);
  }, [offeringId]);

  useEffect(() => {
    if (display || !offeringId) return;
    let active = true;
    fetchBookingPage(offeringId)
      .then((data) => {
        if (!active) return;
        // Render in offering tz when known, falling back to the event tz.
        const bookingTz = data.offering?.timezone ?? eventTz;
        let requestDisplay: BookingDisplayRequest | undefined;
        if (data.myRequest) {
          const r = data.myRequest;
          const windows = (r.availability ?? []).map((w) => {
            const s = new Date(w.startDt);
            const e = new Date(w.endDt);
            const dateStr = s.toLocaleDateString(undefined, { timeZone: bookingTz, weekday: "short", month: "short", day: "numeric" });
            const timeRange = `${s.toLocaleTimeString(undefined, { timeZone: bookingTz, hour: "2-digit", minute: "2-digit" })} – ${e.toLocaleTimeString(undefined, { timeZone: bookingTz, hour: "2-digit", minute: "2-digit" })}`;
            return { dateStr, timeRange };
          });
          requestDisplay = { treatmentNames: r.treatmentNames ?? [], windows };
        }
        if (data.myAppointment) {
          const a = data.myAppointment;
          const start = new Date(a.startDt);
          const end = new Date(a.endDt);
          const dateStr = start.toLocaleDateString(undefined, { timeZone: bookingTz, weekday: "short", month: "short", day: "numeric" });
          const timeStr = `${start.toLocaleTimeString(undefined, { timeZone: bookingTz, hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString(undefined, { timeZone: bookingTz, hour: "2-digit", minute: "2-digit" })}`;
          const appointment: BookingDisplayAppointment = { dateTime: `${dateStr} · ${timeStr}` };
          if (a.treatmentName) appointment.treatmentName = a.treatmentName;
          if (a.status) appointment.status = a.status;
          loadedFromBackend.current = true;
          setDisplay({ mode: "APPOINTMENT", appointment, ...(requestDisplay ? { request: requestDisplay } : {}) });
        } else if (requestDisplay) {
          loadedFromBackend.current = true;
          setDisplay({ mode: "REQUEST", request: requestDisplay });
        }
      })
      .catch(() => { /* ignore — booking data is optional */ });
    return () => { active = false; };
  }, [offeringId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!display) return null;

  return (
    <div
      className={classNames("flex flex-col gap-2 p-3", roundedClasses)}
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: roundedClasses ? undefined : rounding,
      }}
    >
      {(display.mode === "REQUEST" || (display.mode === "APPOINTMENT" && display.request)) ? (() => {
        const req = display.mode === "REQUEST" ? display.request : display.request!;
        return (
          <>
            {req.treatmentNames.length > 0 ? (
              <div className="flex justify-between items-baseline gap-4">
                <span className="text-sm font-medium shrink-0" style={{ color: labelColor, opacity: 0.7 }}>
                  {t("slottingSelectTreatments")}
                </span>
                <span className="text-sm text-right truncate" style={{ color: valueColor }}>
                  {req.treatmentNames.join(", ")}
                </span>
              </div>
            ) : null}
            {req.windows.length > 0 ? (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium shrink-0" style={{ color: labelColor, opacity: 0.7 }}>
                  {t("slottingYourAvailability")}
                </span>
                {req.windows.map((w, i) => (
                  <div key={i} className="flex justify-between items-baseline gap-4 pl-2">
                    <span className="text-sm" style={{ color: labelColor, opacity: 0.5 }}>
                      {w.dateStr}
                    </span>
                    <span className="text-sm text-right" style={{ color: valueColor }}>
                      {w.timeRange}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        );
      })() : null}
      {display.mode === "APPOINTMENT" && display.request ? (
        <hr style={{ borderColor: cardBorder, margin: "4px 0" }} />
      ) : null}
      {display.mode === "APPOINTMENT" ? (
        <>
          {display.appointment.treatmentName ? (
            <div className="flex justify-between items-baseline gap-4">
              <span className="text-sm font-medium shrink-0" style={{ color: labelColor, opacity: 0.7 }}>
                {t("slottingService")}
              </span>
              <span className="text-sm text-right truncate" style={{ color: valueColor }}>
                {display.appointment.treatmentName}
              </span>
            </div>
          ) : null}
          <div className="flex justify-between items-baseline gap-4">
            <span className="text-sm font-medium shrink-0" style={{ color: labelColor, opacity: 0.7 }}>
              {t("slottingDateTime")}
            </span>
            <span className="text-sm text-right truncate" style={{ color: valueColor }}>
              {display.appointment.dateTime}
            </span>
          </div>
          {display.appointment.status ? (() => {
            const sc: Record<string, { bg: string; color: string }> = {
              CONFIRMED: { bg: "rgba(34,197,94,0.15)", color: "#16a34a" },
              TENTATIVE: { bg: "rgba(234,179,8,0.15)", color: "#a16207" },
              CANCELLED: { bg: "rgba(239,68,68,0.15)", color: "#dc2626" },
              CHECKED_IN: { bg: "rgba(59,130,246,0.15)", color: "#2563eb" },
            };
            const s = sc[display.appointment.status] ?? { bg: "rgba(100,116,139,0.15)", color: "#475569" };
            const label = display.appointment.status === "TENTATIVE" ? t("slottingTentative")
              : display.appointment.status === "CONFIRMED" ? t("slottingConfirmed")
              : display.appointment.status.replace("_", " ");
            return (
              <div className="flex justify-between items-center gap-4">
                <span className="text-sm font-medium shrink-0" style={{ color: labelColor, opacity: 0.7 }}>
                  {t("slottingAppointmentStatus")}
                </span>
                <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: s.bg, color: s.color }}>
                  {label}
                </span>
              </div>
            );
          })() : null}
        </>
      ) : null}
    </div>
  );
}

/** Renders booking summary cards for all offering IDs */
function BookingSummaryCard({
  palette,
  rounding,
  roundedClasses,
  cardBg,
  cardBorder,
  labelColor,
  valueColor,
  offeringIds,
}: {
  palette: { primary: string; secondary: string };
  rounding: string;
  roundedClasses: string;
  cardBg: string;
  cardBorder: string;
  labelColor: string;
  valueColor: string;
  offeringIds: string[];
}) {
  if (offeringIds.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <p
        className="text-sm font-semibold"
        style={{ color: palette.primary, margin: 0 }}
      >
        {t("bookingDetails")}
      </p>
      {offeringIds.map((oid) => (
        <SingleBookingSummaryCard
          key={oid}
          rounding={rounding}
          roundedClasses={roundedClasses}
          cardBg={cardBg}
          cardBorder={cardBorder}
          labelColor={labelColor}
          valueColor={valueColor}
          offeringId={oid}
        />
      ))}
    </div>
  );
}

/** Convert an in-memory BookingSummary event to the unified display model. */
function summaryToDisplay(s: BookingSummary): BookingDisplayData {
  if (s.mode === "AUTO_BOOKABLE") {
    const appt: BookingDisplayAppointment = { dateTime: s.dateTime };
    if (s.treatmentName) appt.treatmentName = s.treatmentName;
    if (s.status) appt.status = s.status;
    return { mode: "APPOINTMENT", appointment: appt };
  }
  const windows = (s.availabilityWindows ?? []).map((w) => {
    const dateStr = new Date(w.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    return { dateStr, timeRange: `${w.startTime} – ${w.endTime}` };
  });
  return { mode: "REQUEST", request: { treatmentNames: s.treatmentNames ?? [], windows } };
}

function VerificationOverview({
  snapshot,
  session,
  labelMap,
  palette,
  rounding,
  title,
  subtitle,
  offeringIds,
}: {
  snapshot: CollectedFieldValues | null;
  session: unknown;
  labelMap: Record<string, string>;
  palette: { primary: string; secondary: string };
  rounding: string;
  title?: string | undefined;
  subtitle?: string | undefined;
  offeringIds?: string[];
}) {
  if (!snapshot) return null;

  const checkboxes = snapshot.checkboxes;
  // Build set of checkbox field variables so we don't show them twice
  // (they already appear in the checkboxes card below)
  const checkboxVarSet = new Set(
    checkboxes
      .map((cb) => cb.inputFieldVariable)
      .filter((v): v is string => Boolean(v)),
  );
  const values = Object.entries(snapshot.values).filter(
    ([key]) => !checkboxVarSet.has(key),
  );
  // Resolve session companions to merge acceptanceState into snapshot companions
  const sessionCompanions: Array<Record<string, unknown>> = (() => {
    if (!session || typeof session !== "object") return [];
    const s = session as Record<string, unknown>;
    const user = s.user;
    const comps =
      (user && typeof user === "object"
        ? (user as Record<string, unknown>).companions
        : undefined) ?? s.companions;
    return Array.isArray(comps)
      ? comps.filter(
          (c): c is Record<string, unknown> =>
            c != null && typeof c === "object",
        )
      : [];
  })();

  const companions = snapshot.companions
    .map((c, i) => {
      // Merge acceptanceState from session if not in snapshot
      const sessionComp = sessionCompanions[i];
      const state =
        typeof c.acceptanceState === "string"
          ? c.acceptanceState
          : sessionComp
            ? String(sessionComp.acceptanceState ?? "")
            : "";
      return state ? { ...c, acceptanceState: state } : c;
    })
    .filter((c) =>
      Object.values(c).some((v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === "string") return v.trim().length > 0;
        return true;
      }),
    );
  const roundedClasses = extractUtilityClasses(rounding, "rounded");
  const labelColor = palette.secondary;
  const valueColor = palette.primary;
  const cardBg = palette.secondary + "0a";
  const cardBorder = palette.secondary + "40";

  return (
    <div className="flex flex-col gap-4 w-full">
      <div>
        <h2
          className="text-base font-semibold"
          style={{ color: palette.primary, margin: 0 }}
        >
          {title || t("verificationPage")}
        </h2>
        <p
          className="text-sm mt-1"
          style={{ color: labelColor, opacity: 0.7, margin: 0 }}
        >
          {subtitle || t("checkSubmittedData")}
        </p>
      </div>

      {values.length > 0 ? (
        <div
          className={classNames("flex flex-col gap-2 p-3", roundedClasses)}
          style={{
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: roundedClasses ? undefined : rounding,
          }}
        >
          {values.map(([key, value]) => (
            <div
              key={key}
              className="flex justify-between items-baseline gap-4"
            >
              <span
                className="text-sm font-medium shrink-0"
                style={{ color: labelColor, opacity: 0.7 }}
              >
                {labelMap[key] ?? key}
              </span>
              <span
                className="text-sm text-right truncate"
                style={{ color: valueColor }}
              >
                {formatOverviewValue(value)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {companions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p
            className="text-sm font-semibold"
            style={{ color: palette.primary, margin: 0 }}
          >
            {t("companions")}
          </p>
          {companions.map((companion, idx) => {
            const companionState =
              typeof companion.acceptanceState === "string" &&
              companion.acceptanceState
                ? companion.acceptanceState
                : typeof companion.state === "string" && companion.state
                  ? companion.state
                  : undefined;
            return (
              <div
                key={idx}
                className={classNames(
                  "flex flex-col gap-2 p-3",
                  roundedClasses,
                )}
                style={{
                  backgroundColor: cardBg,
                  border: `1px solid ${cardBorder}`,
                  borderRadius: roundedClasses ? undefined : rounding,
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  {companionState ? (
                    <CompanionStateBadge
                      state={companionState}
                      secondaryColor={palette.secondary}
                    />
                  ) : null}
                  <span
                    className="text-xs font-medium"
                    style={{ color: labelColor, opacity: 0.5 }}
                  >
                    {t("companionLabel", String(idx + 1))}
                  </span>
                </div>
                {Object.entries(companion)
                  .filter(
                    ([key]) =>
                      key !== "inviteeId" &&
                      key !== "acceptanceState" &&
                      key !== "state",
                  )
                  .map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between items-baseline gap-4"
                    >
                      <span
                        className="text-sm font-medium shrink-0"
                        style={{ color: labelColor, opacity: 0.7 }}
                      >
                        {labelMap[key] ?? key}
                      </span>
                      <span
                        className="text-sm text-right truncate"
                        style={{ color: valueColor }}
                      >
                        {formatOverviewValue(value)}
                      </span>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      ) : null}

      <BookingSummaryCard
        palette={palette}
        rounding={rounding}
        roundedClasses={roundedClasses}
        cardBg={cardBg}
        cardBorder={cardBorder}
        labelColor={labelColor}
        valueColor={valueColor}
        offeringIds={offeringIds ?? []}
      />

      {checkboxes.length > 0 ? (
        <div
          className={classNames("flex flex-col gap-2 p-3", roundedClasses)}
          style={{
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: roundedClasses ? undefined : rounding,
          }}
        >
          {checkboxes.map((item, idx) => (
            <div
              key={`${item.text}-${idx}`}
              className="flex justify-between items-baseline gap-4"
            >
              <span
                className="text-sm shrink-0"
                style={{ color: labelColor, opacity: 0.7 }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.text) }}
              />
              <span
                className="text-sm text-right"
                style={{ color: valueColor }}
              >
                {item.checked ? t("yes") : t("no")}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function DataInput(props: RuntimeProps) {
  const { session, currentColorPalette, currentRounding, fields } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const eventTz = useEventTimezone();
  // "This guest has already handed something in." Independent of
  // `allowUpdate`, which answers a different question — whether they may
  // still CHANGE it.
  const hasAlreadySubmitted =
    resolveDataInputSubmittedFlag(session) || isDataInputSubmittedLocally(session);
  // ⚠ Computed and not yet read — left in place, renamed to the repo's
  // allowed-unused form so `npm run lint` (a deploy gate) passes. It arrived
  // with in-flight work from another session; whoever picks that up should
  // either use it or delete it.
  const _initialSubmitted =
    ((props.allowUpdate as boolean | undefined) ?? false) && hasAlreadySubmitted;
  void _initialSubmitted;
  // The organizer's own "changes are closed" sentence, delivered on the
  // session since 2026-08-14 and already resolved into this guest's language.
  // Shown INSTEAD of the generic success line, because a guest arriving on a
  // read-only form needs to know why it is read-only and what to do about it
  // — "Submitted successfully" answers neither. Empty or absent falls back to
  // the generic wording, so an organizer who wrote nothing loses nothing.
  const organizerClosedMessage =
    isPlainRecord(session) && typeof session.resubmitBlockMessage === "string"
      ? session.resubmitBlockMessage.trim()
      : "";
  // Seeded on `hasAlreadySubmitted`, NOT on `initialSubmitted`. With
  // "allow update" switched off, `isReadOnlyVerification` renders the guest's
  // answer as read-only fields with no submit button — correct, but until
  // 2026-08-14 completely unexplained: no heading, no message, just a form
  // that would not budge. Whichever of the two post-submit views they land
  // in, they now get the same sentence telling them why and whom to ask.
  const [state, setState] = useState<SubmitState>(
    hasAlreadySubmitted
      ? {
          loading: false,
          success: organizerClosedMessage || t("submitSuccess"),
        }
      : { loading: false },
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationSnapshot, setVerificationSnapshot] =
    useState<CollectedFieldValues | null>(null);
  const [draftSnapshot, setDraftSnapshot] =
    useState<CollectedFieldValues | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const {
    editorEnabled,
    acceptanceFieldProps,
    fieldLabelMap,
    overviewTitle,
    overviewSubtitle,
    companionFieldVariables,
    checkboxItems,
    bookingGroups,
  } = useEditor((editorState) => {
    let acceptanceProps: Record<string, unknown> | undefined;
    const labels: Record<string, string> = {};
    let resolvedOverviewTitle: string | undefined;
    let resolvedOverviewSubtitle: string | undefined;
    const companionVars = new Set<string>();
    let resolvedCheckboxItems: Array<{
      text: string;
      required: boolean;
      inputFieldVariable?: string;
    }> = [];
    const resolvedBookingGroups: { offeringId: string; nodeId: string }[] = [];
    try {
      const nodes = editorState.nodes;
      for (const nodeId in nodes) {
        const node = nodes[nodeId];
        if (!node?.data?.props) continue;
        const p = node.data.props as Record<string, unknown>;
        const variable = p.inputFieldVariable;
        if (typeof variable === "string" && variable) {
          const label = p.label;
          if (typeof label === "string" && label) {
            // Canonicalised, so a page that DECLARES `acceptanceState` still
            // registers its label under the key the answer is submitted with.
            // Otherwise `fieldLabelMap` — and through it `sessionSnapshot`
            // below — is keyed differently from `values`, and the "nothing
            // changed" short-circuit reports a change on every resubmit.
            labels[canonicalFieldVariable(variable)] = label;
          }
          if (p.type === "companion") {
            companionVars.add(variable);
          }
        }
        if (variable === "state" || variable === "acceptanceState") {
          acceptanceProps = p;
        }
        const displayName = node.data.displayName;
        if (displayName === "overviewTitle" && typeof p.text === "string") {
          resolvedOverviewTitle = p.text;
        }
        if (displayName === "overviewText" && typeof p.text === "string") {
          resolvedOverviewSubtitle = p.text;
        }
        // Extract checkbox definitions from the Checkboxes node
        if (
          displayName === "Checkboxes" &&
          Array.isArray(p.checkboxes)
        ) {
          resolvedCheckboxItems = (
            p.checkboxes as Array<Record<string, unknown>>
          ).map((cb) => {
            const base = {
              text: resolveText(cb.text as LangText, currentLang, primaryLanguage),
              required: Boolean(cb.required),
            };
            return typeof cb.inputFieldVariable === "string"
              ? { ...base, inputFieldVariable: cb.inputFieldVariable }
              : base;
          });
        }
        // Collect all BookingGroup nodes with their offeringIds
        if (
          node.data.displayName === "BookingGroup" &&
          typeof p.offeringId === "string" &&
          p.offeringId
        ) {
          resolvedBookingGroups.push({ offeringId: p.offeringId, nodeId });
        }
      }
    } catch {
      // ignore
    }
    return {
      editorEnabled: editorState.options.enabled,
      acceptanceFieldProps: acceptanceProps,
      fieldLabelMap: labels,
      overviewTitle: resolvedOverviewTitle,
      overviewSubtitle: resolvedOverviewSubtitle,
      companionFieldVariables: Array.from(companionVars),
      checkboxItems: resolvedCheckboxItems,
      bookingGroups: resolvedBookingGroups,
    };
  });
  const previousEditorEnabled = useRef(editorEnabled);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Computed here, RETURNED far below (just above the JSX). Five hooks live
  // between this point and the render — useMemo x3 and useEffect x2 — so
  // returning here ran five hooks fewer than the previous render and React threw
  // "Rendered fewer hooks than expected". On a published site that is a white
  // screen on the registration form itself: `visibilityFilter` re-evaluates
  // visibility every render, so an answer that hides this section does it to an
  // already-mounted component.
  const sectionVisible = isVisible(props.isVisible as boolean | undefined);

  const allowUpdate = (props.allowUpdate as boolean | undefined) ?? false;
  const isVisibleHeadline =
    (props.isVisibleHeadline as boolean | undefined) ?? true;
  const isVisibleHeader =
    (props.isVisibleHeader as boolean | undefined) ?? true;
  const isVisibleText = (props.isVisibleText as boolean | undefined) ?? true;
  const hasVisibleText = isVisibleHeadline || isVisibleHeader || isVisibleText;
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const textSpacing = props.textSpacing as string | undefined;
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const progressBarEnabled = Boolean(props.progressBarEnabled);
  const progressBarColor =
    (props.progressBarColor as string | undefined) ??
    currentColorPalette.secondary;
  // AUDIT 2026-08-14 — ALWAYS `state`, whatever the page declares.
  //
  // This used to be `acceptanceFieldProps?.inputFieldVariable ??
  // "acceptanceState"`, and no template, wizard or DSL path has ever emitted
  // an acceptance node — so every generated site fell into that fallback and
  // posted its Zusage under `acceptanceState`, which the worker drops
  // silently (DROPPED_CORE_KEYS: it is the ORGANISER's verdict on a request,
  // and a visitor must not be able to approve their own). Result: 200, no
  // state written, no "Gast hat zugesagt" automation, nothing in the history.
  // Reported twice before it was found, because `App.tsx` STATUS_ALIASES
  // mirrors the answer across all three spellings in the live visibility map,
  // so the form REVEALS correctly — the UI looked perfect and only the write
  // was missing.
  //
  // `acceptanceFieldProps` is still read for labels, colours and button text
  // — the node keeps describing the question, it just no longer names the
  // wire key. Cannot double-post: a FormInput declaring either acceptance
  // variable renders nothing (`formInput.tsx` isAcceptanceField → null) and
  // DataInputAcceptanceField is rendered unconditionally, so exactly one
  // element ever carries this attribute.
  const acceptanceFieldVariable = ACCEPTANCE_SUBMIT_VARIABLE;
  const multiStepEnabled = Boolean(props.multiStepEnabled);
  const multiStepMode =
    (props.multiStepMode as "pages" | "single" | undefined) ?? "pages";
  const overviewEnabled = Boolean(props.overviewEnabled);
  const alreadySubmittedOnServer =
    resolveDataInputSubmittedFlag(session) ||
    isDataInputSubmittedLocally(session);

  // The summary prints `labelMap[key] ?? key`, so a key with no declared label
  // shows the visitor a raw internal identifier. The acceptance question is
  // exactly that case for two whole lanes: the DSL compiler plants the node
  // without a label (it has no translator in scope), and a page may declare no
  // node at all — the answer is still submitted either way. Fall back to the
  // same label the widget draws for itself; anything a page DID declare still
  // wins.
  const overviewLabelMap = useMemo(
    () => ({ [ACCEPTANCE_SUBMIT_VARIABLE]: t("status"), ...fieldLabelMap }),
    [fieldLabelMap],
  );

  const sessionSnapshot = useMemo(() => {
    if (!alreadySubmittedOnServer) return null;
    // The acceptance answer is ALWAYS submitted, whether or not the page
    // declares a node for it — and no published wizard site declares one, so
    // `fieldLabelMap` would not contain it and the snapshot would be missing
    // the one key every resubmit carries. That reads as "something changed"
    // on every single resubmit. Seeded unconditionally for that reason; the
    // Set collapses the duplicate for the pages that DO declare it.
    const fieldVars = Array.from(
      new Set([ACCEPTANCE_SUBMIT_VARIABLE, ...Object.keys(fieldLabelMap)]),
    );
    if (fieldVars.length === 0) return null;
    // Estimate companion count from session
    let companionCount = 0;
    if (session && typeof session === "object") {
      const s = session as Record<string, unknown>;
      const user = s.user;
      const companions =
        (user && typeof user === "object"
          ? (user as Record<string, unknown>).companions
          : undefined) ?? s.companions;
      if (Array.isArray(companions)) companionCount = companions.length;
    }
    return buildSessionSnapshot(
      session,
      fieldVars,
      companionCount,
      companionFieldVariables,
      checkboxItems,
    );
  }, [
    alreadySubmittedOnServer,
    session,
    fieldLabelMap,
    companionFieldVariables,
    checkboxItems,
  ]);

  const stepLabels = ((props.stepLabels as LangText[] | undefined) ?? []).map(
    (l) => resolveText(l, currentLang, primaryLanguage),
  );
  const stepSections = (
    (props.stepSections as string[][] | undefined) ?? [
      ["fields-0", "companions"],
    ]
  ).map((item) => item);
  const totalSteps = stepSections.length;
  const effectiveTotal =
    multiStepEnabled && multiStepMode === "pages" && overviewEnabled
      ? totalSteps + 1
      : totalSteps;
  const isOverview =
    multiStepEnabled &&
    multiStepMode === "pages" &&
    overviewEnabled &&
    currentStep === totalSteps;
  const isReadOnlyVerification =
    !allowUpdate && (verificationSnapshot !== null || alreadySubmittedOnServer);
  const isAllowUpdateOverview =
    (allowUpdate && hasSubmitted && verificationSnapshot !== null) ||
    (allowUpdate && alreadySubmittedOnServer && !isEditing);
  const showOverviewPage =
    isOverview || isReadOnlyVerification || isAllowUpdateOverview;

  /** The form has a PRE-submit review step, and where it sits. */
  const showReviewStep =
    !!multiStepEnabled &&
    multiStepMode === "pages" &&
    !!overviewEnabled &&
    !isReadOnlyVerification &&
    !isAllowUpdateOverview;
  const reviewStepIndex = totalSteps;

  // Which action button this step shows — the shared contract, pinned by
  // dataInput.actionRow.test.ts and mirrored in the editor twin. The action row
  // wrapper divs gate on these; an empty wrapper left standing would eat a gap.
  const {
    back: showBackButton,
    next: showNextButton,
    submit: showSubmitButton,
    checkboxes: showCheckboxes,
  } = actionRowVisibility({
    multiStepEnabled: !!multiStepEnabled,
    multiStepMode: multiStepMode === "single" ? "single" : "pages",
    totalSteps,
    hasReviewStep: !!overviewEnabled,
    currentStep,
    isReadOnlyVerification,
    isAllowUpdateOverview,
  });

  const visibleSections = useMemo(() => {
    if (showOverviewPage) return new Set<string>();
    if (!multiStepEnabled) return new Set(stepSections.flat());
    if (multiStepMode === "single") return new Set(stepSections.flat());
    return new Set(stepSections[currentStep] ?? []);
  }, [
    multiStepEnabled,
    multiStepMode,
    showOverviewPage,
    stepSections,
    currentStep,
  ]);
  const allStepSections = useMemo(
    () => Array.from(new Set(stepSections.flat())),
    [stepSections],
  );

  function captureDraftFromForm(
    form: HTMLFormElement | null = formRef.current,
  ) {
    if (!form) return null;
    const snapshot = collectFieldValues(form, eventTz);
    setDraftSnapshot(snapshot);
    return snapshot;
  }

  /** 2026-10-01: the mount/step capture below. Same DOM read, but marked
   *  `pristine` while nothing the visitor did has been captured yet (no draft,
   *  or only an earlier pristine one) — every interaction path (input, change,
   *  Back/Next, step pills, submit) goes through `captureDraftFromForm` or sets
   *  the draft outright and so drops the mark. Without it the read, taken in
   *  the commit the Checkboxes widget only scheduled its session prefill, sent
   *  a stored `emailOptIn: true` out as `false` on an untouched submit. */
  function capturePristineDraftFromForm() {
    const form = formRef.current;
    if (!form) return;
    const snapshot = collectFieldValues(form, eventTz);
    setDraftSnapshot((prev) =>
      prev === null || prev.pristine ? { ...snapshot, pristine: true } : snapshot,
    );
  }

  function dispatchValidation(
    form: HTMLFormElement | null = formRef.current,
    /** Validate the fields of INACTIVE steps as well (they stay mounted with
     *  `display:none`, and each field opts itself out of a normal pass). Used
     *  by submit: `goNext` gates one step at a time, but the numbered pills
     *  let a visitor jump straight to the last step, so without this the
     *  skipped steps' required fields were checked by nobody — an empty name
     *  and email sailed through. On failure the caller jumps to the step that
     *  holds the first error. */
    includeHidden = false,
  ): boolean {
    if (!form) return true;
    const detail = {
      errorCount: 0,
      includeHidden,
      hiddenErrorNodes: [] as Element[],
    };
    form.dispatchEvent(new CustomEvent(WB_VALIDATE_EVENT, { detail }));
    const outcome = decideValidationOutcome(
      detail.errorCount,
      detail.hiddenErrorNodes.map(stepIndexOf),
    );
    if (outcome.jumpToStep !== undefined && outcome.jumpToStep !== currentStep)
      setCurrentStep(outcome.jumpToStep);
    return outcome.pass;
  }

  useEffect(() => {
    if (!previousEditorEnabled.current && editorEnabled) {
      setHasSubmitted(false);
      setIsEditing(false);
      setVerificationSnapshot(null);
      setDraftSnapshot(null);
      setCurrentStep(0);
      setState({ loading: false });
    }
    previousEditorEnabled.current = editorEnabled;
  }, [editorEnabled]);

  useEffect(() => {
    // Skip capturing draft on mount when already submitted — the sessionSnapshot
    // handles the overview display and the form checkboxes may not yet reflect
    // session values (race condition with Checkboxes useEffect).
    if (alreadySubmittedOnServer && !isEditing) return;
    // A hidden section renders no form, so formRef is null and this would be a
    // no-op anyway — but opting out explicitly keeps it that way if the ref ever
    // outlives the render.
    if (!sectionVisible) return;
    capturePristineDraftFromForm();
  }, [currentStep, multiStepEnabled, multiStepMode, overviewEnabled, alreadySubmittedOnServer, isEditing, sectionVisible]);

  function renderStepSection(sectionId: string) {
    if (sectionId.startsWith("fields-")) {
      const idx = Number(sectionId.split("-")[1]);
      return <Element is={FieldsGroup} id={`fieldsGroup${idx}`} canvas />;
    }
    if (sectionId === "companions") {
      return <Element is={CompanionsGroup} id="companionsGroup" canvas />;
    }
    if (sectionId.startsWith("booking-")) {
      const idx = Number(sectionId.split("-")[1]);
      return <Element is={BookingGroup} id={`bookingGroup${idx}`} />;
    }
    // Section-prefix contract: `session-${n}` — MUST match the editor's
    // DataInput renderer (stepSectionsModel) or the step silently no-renders.
    if (sectionId.startsWith("session-")) {
      const idx = Number(sectionId.split("-")[1]);
      return <Element is={SessionGroup} id={`sessionGroup${idx}`} />;
    }
    return null;
  }

  async function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();

    // …including the steps the visitor never opened (pill jump).
    if (!dispatchValidation(event.currentTarget, true)) return;

    // A form with a review step must pass THROUGH it. The submit button only
    // renders there, but implicit submission does not need a button: Enter in
    // any text field submits the form from any step, which used to POST
    // straight past the "please review your information" page. Route the
    // visitor there instead — same move the editor twin makes on its mock
    // submit. Validation above already ran over every step.
    if (showReviewStep && currentStep !== reviewStepIndex) {
      captureDraftFromForm(event.currentTarget);
      setCurrentStep(reviewStepIndex);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    setState({ loading: true });

    const form = event.currentTarget;
    const {
      values,
      companions: rawCompanions,
      checkboxes,
    } = collectFieldValues(form, eventTz);

    // Inject inviteeId from session so existing companions are updated, not duplicated
    const sessionIds = getSessionCompanionIds(session);
    const companions = rawCompanions.map((c, i) => {
      const existingId = sessionIds[i];
      if (existingId && !c.inviteeId) {
        return { ...c, inviteeId: existingId };
      }
      return c;
    });

    const submittedValues = { values, companions, checkboxes };
    setDraftSnapshot(submittedValues);

    // Layer B client-side gate — mirrors backend-website-builder's
    // GuestAdditionalFieldValidator (validateSubmissionAndCompanions). Runs
    // before the HTTP POST so bad input surfaces as a readable error in place
    // instead of a generic 400 after round-trip. Server remains source of
    // truth — this is UX polish.
    //
    // The collected `values` map is flat and mixes core GuestDoc columns
    // (firstName, email, state, …) with custom additional-field keys. The
    // schema only describes additional fields, so we validate the additional
    // subset only — exactly as the backend does after splitDataInputValues.
    // Validating the full map here would mis-report every core column as
    // `unknownAdditionalFieldKey` and block submissions the backend accepts.
    //
    // For companions we validate the pre-injection `rawCompanions` (the
    // collected field values), NOT `companions` — the latter carries an
    // injected `inviteeId` structural id that the backend reads off
    // CompanionInput, not from the values map, so it must not be validated
    // as an additional field.
    const schema = fieldsToSchema(fields);
    if (schema.length > 0) {
      const rendered = renderedFieldVariables(form);
      const mainSubmitted = selectAdditionalFieldValues(values);
      const mainResult = validateSubmissionClientSide(
        mainSubmitted,
        schemaForSubmitted(schema, mainSubmitted, rendered),
      );
      // Required-IMAGE: a stored image + untouched widget (key omitted)
      // still satisfies `required` — mirror the worker's data-input rule
      // so an unrelated edit isn't blocked by the image field.
      const allViolations: AdditionalFieldViolation[] = dropSatisfiedImageRequired(
        [...mainResult.violations],
        schema,
        mainSubmitted,
        (name) => resolveSessionPrefillValue(session, name),
      );
      for (let i = 0; i < rawCompanions.length; i++) {
        const cSubmitted = selectAdditionalFieldValues(rawCompanions[i] ?? {});
        const cRes = validateSubmissionClientSide(
          cSubmitted,
          schemaForSubmitted(schema, cSubmitted),
        );
        const cViolations = dropSatisfiedImageRequired(
          cRes.violations,
          schema,
          cSubmitted,
          (name) => resolveCompanionSessionPrefillValue(session, name, i),
        );
        for (const v of cViolations) {
          allViolations.push({ fieldKey: `companions[${i}].${v.fieldKey}`, code: v.code });
        }
      }
      if (allViolations.length > 0) {
        setState({
          loading: false,
          error: formatViolationsForDisplay(allViolations),
        });
        return;
      }
    }

    // Session-selection (P4): when a `session-${n}` step is configured,
    // collect the picks from the hidden `__session_selected` input(s).
    // `undefined` (no step / widget never mounted / list failed to load) →
    // key omitted → worker leaves existing picks untouched.
    const sessionStepActive = stepSections
      .flat()
      .some((s) => s.startsWith("session-"));
    const sessionChanged = sessionStepActive && hasSessionSelectionChanges(form);
    const sessionsPayload = sessionStepActive
      ? collectSessionsPayload(form)
      : undefined;

    // OFFERING slot bookings (W3, governed-scope contract): same
    // session-step gate — OFFERING cards live inside SessionGroup.
    // `undefined` (no `__offering_selected` input: no OFFERING cards
    // mounted, or no block governed yet) → key omitted → the worker
    // leaves the guest's bookings untouched. When present, the payload
    // scopes the worker's cancel sweep to `governedProgramItemIds` —
    // blocks whose envelope never loaded stay ungoverned and unswept.
    // Computed BEFORE the "noChanges" gate below (F2): a guest changing
    // ONLY their slot has identical field values.
    const offeringBookingsPayload = sessionStepActive
      ? collectOfferingBookingsPayload(form)
      : undefined;

    // When updating existing data, skip submission if nothing changed.
    // Offering bookings count as a change (F2): the widget compares its
    // live selections against the seeded server state (`__offering_dirty`)
    // — without this, a slot-only edit would early-return "noChanges" and
    // silently never reach the worker's booking diff.
    // Session picks count as a change for the same reason (a guest who
    // only ticks a session has identical field values).
    const offeringChanged =
      offeringBookingsPayload !== undefined &&
      hasOfferingSelectionChanges(form);
    if (
      shouldSkipUnchangedSubmit({
        allowUpdate,
        isEditing,
        sessionSnapshot,
        values,
        companions,
        offeringChanged,
        sessionChanged,
      })
    ) {
      setState({ loading: false, success: t("noChanges") });
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    // Compute changedValues for email notification (only fields that differ from session)
    let changedValues: Record<string, unknown> | undefined;
    if (sessionSnapshot) {
      const diff: Record<string, unknown> = {};
      for (const [key, next] of Object.entries(values)) {
        const prev = sessionSnapshot.values[key];
        if (String(prev ?? "") !== String(next ?? "")) {
          diff[key] = next;
        }
      }
      if (Object.keys(diff).length > 0) {
        changedValues = diff;
      }
    }

    // Determine which session companions were removed (present in session but
    // not included in the submitted companions list)
    const submittedIdSet = new Set(
      companions
        .map((c) => String(c.inviteeId ?? ""))
        .filter((id) => id.length > 0),
    );
    const removedCompanionIds = sessionIds.filter(
      (id) => id.length > 0 && !submittedIdSet.has(id),
    );

    try {
      const response = await submitDataInput({
        values,
        companions,
        checkboxes,
        ...(changedValues ? { changedValues } : {}),
        ...(removedCompanionIds.length > 0 ? { removedCompanionIds } : {}),
        // The `!== undefined` is repeated for TS narrowing; the decision
        // itself lives in the pure helper so it can be tested.
        ...(sessionsPayload !== undefined &&
        shouldSendSessions({
          sessionsPayload,
          allowUpdate,
          isEditing,
          sessionChanged,
        })
          ? { sessions: sessionsPayload }
          : {}),
        ...(offeringBookingsPayload !== undefined
          ? { offeringBookings: offeringBookingsPayload }
          : {}),
        step: {
          enabled: multiStepEnabled,
          mode: multiStepMode,
          labels: stepLabels,
          sections: stepSections,
          current: currentStep,
        },
      });
      markDataInputSubmitted(session);
      // The submitted set IS the server state now — SessionGroup clears
      // its explicit-remove flags and re-baselines `__offering_dirty`.
      form.dispatchEvent(new CustomEvent(WB_SUBMIT_SUCCESS_EVENT));

      // If booking section is active, submit ALL booking groups (multi-offering support)
      const bookingActive = stepSections.flat().some((s) => s.startsWith("booking-"));
      let bookingFailed = false;
      if (bookingActive && bookingGroups.length > 0) {
        const bookingResults = await Promise.allSettled(
          bookingGroups.map(async ({ offeringId }) => {
            const bookingMode = form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_mode"]`)?.value;

            if (bookingMode === "REQUEST_BASED") {
              const treatmentIds = JSON.parse(form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_treatmentIds"]`)?.value || "[]") as string[];
              const availability = JSON.parse(form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_availability"]`)?.value || "[]") as { startDt: string; endDt: string }[];
              if (treatmentIds.length > 0 && availability.length > 0) {
                await submitRequest(offeringId, { treatmentIds, availability });
              }
            } else {
              const bookingLaneId = form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_laneId"]`)?.value ?? "";
              const bookingStartDt = form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_startDt"]`)?.value ?? "";
              const bookingEndDt = form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_endDt"]`)?.value ?? "";
              const bookingTreatmentId = form.querySelector<HTMLInputElement>(`input[name="__booking_${offeringId}_treatmentId"]`)?.value ?? "";
              if (bookingLaneId && bookingStartDt && bookingEndDt) {
                await submitBooking(offeringId, {
                  ...(bookingTreatmentId ? { treatmentId: bookingTreatmentId } : {}),
                  laneId: bookingLaneId,
                  startDt: bookingStartDt,
                  endDt: bookingEndDt,
                });
              }
            }
          }),
        );
        // Report errors per offering
        for (let i = 0; i < bookingResults.length; i++) {
          const result = bookingResults[i];
          if (result && result.status === "rejected") {
            bookingFailed = true;
            const reason = result.reason;
            formRef.current?.dispatchEvent(
              new CustomEvent(WB_BOOKING_ERROR_EVENT, {
                detail: {
                  offeringId: bookingGroups[i]?.offeringId,
                  message: reason instanceof Error ? reason.message : "",
                },
              }),
            );
          }
        }
      }

      if (!allowUpdate) {
        setHasSubmitted(true);
        setVerificationSnapshot(submittedValues);
        if (multiStepEnabled && multiStepMode === "pages" && overviewEnabled) {
          setCurrentStep(totalSteps);
        }
        setState({ loading: false, ...(bookingFailed ? { warning: t("bookingSubmissionFailed") } : {}) });
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      setHasSubmitted(true);
      setIsEditing(false);
      setVerificationSnapshot(submittedValues);
      setState({
        loading: false,
        success: String(response.message ?? t("submitSuccess")),
        ...(bookingFailed ? { warning: t("bookingSubmissionFailed") } : {}),
      });
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (error) {
      // 409 sessionFull: chosen session(s) filled up in a race. Relay the
      // full ids to the SessionGroup (marks cards sold-out, prompts a
      // re-pick) and surface a readable group-level error. Nothing durable
      // was saved server-side: the guest tx (values, companions, session
      // picks) aborted atomically, offering bookings created before it are
      // compensated (reschedules reinstated best-effort), and governed
      // sweep-cancels only ever run AFTER commit (the worker's post-commit
      // `executeSweepCancels`) — so existing bookings survive the abort.
      // 403 capacity refusals — a full segment quota or the companion cap.
      // Same treatment as on the register form: neutral, localised, and
      // without the organiser's internal segment name, which the worker's raw
      // `message` used to carry straight to the guest.
      if (error instanceof ApiError && error.status === 403) {
        const refusal = parseCapacityRefusal(error.data);
        if (refusal !== null) {
          setState({
            loading: false,
            error:
              refusal.kind === "quotaFull"
                ? t("registrationSegmentFull")
                : t("companionLimitReached"),
          });
          return;
        }
      }
      if (error instanceof ApiError && error.status === 409) {
        // 409 resubmitBlocked — the organizer closed changes, either with the
        // switch or with the deadline. Added 2026-08-14: this code has been
        // the Worker's answer since the block shipped, and the runtime had no
        // branch for it, so the guest filled in the whole form and got a
        // generic failure with the raw error code in it. The Worker resolves
        // the organizer's own message into the GUEST's language, so relay it
        // verbatim when present rather than inventing a second wording.
        //
        // Also flip the form into its submitted state — which this component
        // expresses as `state.success` — because the refusal is proof that an
        // answer is already on file, which is exactly what that view says.
        // Without it the guest is left staring at a form they can only keep
        // failing to send.
        const resubmitMessage = parseResubmitBlockedError(error.data);
        if (resubmitMessage !== null) {
          markDataInputSubmitted(session);
          setState({
            loading: false,
            success:
              resubmitMessage.length > 0
                ? resubmitMessage
                : organizerClosedMessage || t("submitSuccess"),
          });
          return;
        }
        // PB-N2-2 — 409 optionCapacityFull: an OPTION the guest picked (not a
        // session, not the event) sold out in a race. The envelope has always
        // carried `fieldId` + `option`; the runtime dropped them and rendered
        // the bare `message`, so the guest saw the literal string
        // `optionCapacityFull` in the form alert with no hint as to which of
        // their choices had to change. Name the option when we have it and
        // fall back to the generic phrasing when the envelope omits it.
        const optionFull = parseOptionCapacityFullError(error.data);
        if (optionFull !== null) {
          setState({
            loading: false,
            error: optionFull.option
              ? t("optionCapacityFull", optionFull.option)
              : t("optionCapacityFullGeneric"),
          });
          return;
        }
        const fullProgramItemIds = parseSessionFullError(error.data);
        if (fullProgramItemIds !== null) {
          formRef.current?.dispatchEvent(
            new CustomEvent(WB_SESSION_FULL_EVENT, {
              detail: { fullProgramItemIds },
            }),
          );
          setState({ loading: false, error: t("sessionFullError") });
          return;
        }
        // 409 offeringSlotConflict: the picked slot filled up / vanished
        // in a race. No durable change server-side: the conflict aborts
        // inside the booking diff BEFORE the guest write, bookings it had
        // already created self-compensate, and sweep-cancels are plan-only
        // until post-commit — existing bookings stay. Relay to the
        // SessionGroup (marks the block, refetches its slots, forces a
        // re-pick) — mirror of the sessionFull wiring above.
        const offeringConflict = parseOfferingConflictError(error.data);
        if (offeringConflict !== null) {
          formRef.current?.dispatchEvent(
            new CustomEvent(WB_OFFERING_CONFLICT_EVENT, {
              detail: { ...offeringConflict, reason: "conflict" },
            }),
          );
          setState({ loading: false, error: t("offeringConflictError") });
          return;
        }
        // 409 offeringMultipleAppointments (H1): the guest holds ≥2
        // active appointments in the block's container — an admin-managed
        // package this form cannot express. The relayed refetch flips the
        // card into its non-interactive "multiple appointments" info
        // state (the fresh envelope carries `multipleAppointments: true`)
        // and drops the block from the governed set.
        const multiAppointmentsId = parseOfferingMultipleAppointmentsError(
          error.data,
        );
        if (multiAppointmentsId !== null) {
          formRef.current?.dispatchEvent(
            new CustomEvent(WB_OFFERING_CONFLICT_EVENT, {
              detail: {
                programItemId: multiAppointmentsId,
                code: "",
                reason: "multipleAppointments",
              },
            }),
          );
          setState({
            loading: false,
            error: t("offeringMultipleAppointmentsError"),
          });
          return;
        }
      }
      // 400 offeringNotDirectlyBookable: REQUEST_BASED offering was
      // direct-booked (stale envelope). Surface as a card-level error —
      // the relayed refetch flips the card into its "on request" state.
      if (error instanceof ApiError && error.status === 400) {
        const notBookableId = parseOfferingNotBookableError(error.data);
        if (notBookableId !== null) {
          formRef.current?.dispatchEvent(
            new CustomEvent(WB_OFFERING_CONFLICT_EVENT, {
              detail: {
                programItemId: notBookableId,
                code: "",
                reason: "notBookable",
              },
            }),
          );
          setState({ loading: false, error: t("offeringNotBookableError") });
          return;
        }
      }
      // Fix round 1 (l2 F-3 / l8 W-2): the Worker's `400 {errorCode:
      // "invalidFieldType"}` has no message, so `error.message` was the
      // status fallback "Request failed with status 400"; 401/429/503 were
      // raw English. Same mapping as register/login/contact — a bare 401
      // here is a lost session (`requireSession`), not a wrong code.
      setState({
        loading: false,
        error: submitErrorText(error, t("submitFailed"), { unauthorizedKey: "sessionExpired" }),
      });
    }
  }

  // The return deferred from above: every hook has now run, so the count is
  // identical whether or not this section is visible.
  if (!sectionVisible) return null;

  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={props.padding as string | undefined}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={props.reveal as WBReveal | undefined}
    >
      <DataInputNavigationContext.Provider
        value={{
          goBack: () => {
            if (isReadOnlyVerification) return;
            captureDraftFromForm();
            setCurrentStep((v) => Math.max(v - 1, 0));
          },
          goNext: () => {
            if (isReadOnlyVerification) return;
            if (!dispatchValidation()) return;
            captureDraftFromForm();
            setCurrentStep((v) => Math.min(v + 1, effectiveTotal - 1));
          },
        }}
      >
        <DataInputReadOnlyContext.Provider value={isReadOnlyVerification}>
          <DataInputDraftContext.Provider value={draftSnapshot}>
            <DataInputVerificationContext.Provider value={verificationSnapshot}>
              <form
                ref={formRef}
                noValidate
                onSubmit={onSubmit}
                onInput={() => {
                  captureDraftFromForm();
                }}
                onChange={() => {
                  captureDraftFromForm();
                }}
                className={classNames(
                  "max-w-275 flex flex-col w-full",
                  hasVisibleText ? "gap-5" : "",
                  alignmentClass,
                )}
              >
                <div
                  className={classNames(
                    "flex flex-col w-full",
                    textSpacing ?? "space-y-1 wb-md:space-y-2 wb-lg:space-y-3",
                  )}
                >
                  {/* Audit 2026-09-23 (4b): the header's TextChild nodes are
                      saved with the page, so the `isSubmitted` seed below never
                      reached them after the first save — the "thank you" copy
                      never showed. The context is what they follow now. */}
                  <SubmitStateContext.Provider
                    value={{ isSubmitted: hasSubmitted, loading: state.loading }}
                  >
                    <SectionHeader
                      isSubmitted={hasSubmitted}
                      enableSubmittedState
                    />
                  </SubmitStateContext.Provider>
                </div>

                {multiStepEnabled &&
                multiStepMode === "pages" &&
                !isAllowUpdateOverview ? (
                  <div className="flex flex-col items-center gap-2 w-full">
                    <div className="flex items-center gap-2">
                      {Array.from({ length: effectiveTotal }).map((_, idx) => {
                        const resolvedStepLabel =
                          mapOverviewLabelToVerification(
                            stepLabels[idx] ||
                              (overviewEnabled && idx === totalSteps
                                ? t("verificationPage")
                                : stepSections[idx]?.some((s) =>
                                      s.startsWith("session-"),
                                    )
                                  ? t("sessionStepLabel")
                                  : ""),
                          );

                        const isActive = idx === currentStep;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              if (isReadOnlyVerification) return;
                              // Forward jumps validate exactly like "Next" —
                              // the pills used to be a free bypass around
                              // `goNext`'s gate. Going BACK stays free.
                              if (idx > currentStep && !dispatchValidation())
                                return;
                              captureDraftFromForm();
                              setCurrentStep(idx);
                            }}
                            disabled={isReadOnlyVerification}
                            className={classNames(
                              "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all",
                              isReadOnlyVerification
                                ? "opacity-70"
                                : isActive
                                  ? "opacity-100"
                                  : "opacity-40 hover:opacity-70",
                            )}
                            style={{
                              backgroundColor: isActive
                                ? currentColorPalette.secondary
                                : undefined,
                              color: isActive
                                ? "#fff"
                                : currentColorPalette.primary,
                              border: isActive
                                ? undefined
                                : `1px solid ${currentColorPalette.secondary}`,
                              cursor: isReadOnlyVerification
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            <span>{idx + 1}</span>
                            {resolvedStepLabel ? (
                              <span>{resolvedStepLabel}</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {multiStepEnabled &&
                progressBarEnabled &&
                !isAllowUpdateOverview ? (
                  <div className="w-full">
                    <div
                      className="w-full h-1.5 rounded-full overflow-hidden"
                      style={{
                        backgroundColor: progressBarColor + "1a",
                      }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-300 ease-in-out"
                        style={{
                          width:
                            multiStepMode === "pages"
                              ? `${((currentStep + 1) / effectiveTotal) * 100}%`
                              : "100%",
                          backgroundColor: progressBarColor,
                        }}
                      />
                    </div>
                  </div>
                ) : null}

                <div
                  // The participation question lives OUTSIDE the step wrappers
                  // but is gated on `fields-0`; without this anchor a submit
                  // that trips its (always-on) required check would have no
                  // step to jump to. -1 when the form has no `fields-0` step
                  // at all — then the field is unreachable and the submit gate
                  // deliberately lets the server decide.
                  data-wb-step={stepSections.findIndex((group) =>
                    group.includes("fields-0"),
                  )}
                  style={{
                    display:
                      !showOverviewPage && visibleSections.has("fields-0")
                        ? undefined
                        : "none",
                  }}
                >
                  <DataInputAcceptanceField
                    fieldVariable={acceptanceFieldVariable}
                    session={session}
                    secondaryColor={currentColorPalette.secondary}
                    labelColor={currentColorPalette.primary}
                    borderColor={currentColorPalette.secondary}
                    rounding={currentRounding}
                    readOnly={isReadOnlyVerification}
                    errorColor="#ef4444"
                    acceptButtonText={
                      acceptanceFieldProps?.acceptButtonText as
                        | string
                        | undefined
                    }
                    declineButtonText={
                      acceptanceFieldProps?.declineButtonText as
                        | string
                        | undefined
                    }
                    selectedButtonColor={
                      acceptanceFieldProps?.selectedButtonColor as
                        | string
                        | undefined
                    }
                    selectedButtonBackground={
                      acceptanceFieldProps?.selectedButtonBackground as
                        | string
                        | undefined
                    }
                    hoverButtonColor={
                      acceptanceFieldProps?.hoverButtonColor as
                        | string
                        | undefined
                    }
                    hoverButtonBackground={
                      acceptanceFieldProps?.hoverButtonBackground as
                        | string
                        | undefined
                    }
                    inputFontColor={
                      acceptanceFieldProps?.inputFontColor as string | undefined
                    }
                    backgroundColor={
                      acceptanceFieldProps?.backgroundColor as
                        | string
                        | undefined
                    }
                  />
                </div>

                {allStepSections.map((sectionId) => {
                  const showDivider =
                    multiStepEnabled &&
                    multiStepMode === "single" &&
                    stepSections.some((group) => group[0] === sectionId);
                  const stepIndex = showDivider
                    ? stepSections.findIndex((group) => group[0] === sectionId)
                    : -1;
                  const stepLabel =
                    stepIndex >= 0
                      ? stepLabels[stepIndex] ||
                        (stepSections[stepIndex]?.some((s) =>
                          s.startsWith("session-"),
                        )
                          ? t("sessionStepLabel")
                          : "")
                      : "";

                  return (
                    <div
                      key={sectionId}
                      // Which STEP this section belongs to. Submit validates
                      // hidden steps too (a pill jump can skip one), and this
                      // is how it knows where to send the visitor when a field
                      // on another step is the one that failed.
                      data-wb-step={stepSections.findIndex((group) =>
                        group.includes(sectionId),
                      )}
                      style={{
                        display: visibleSections.has(sectionId)
                          ? undefined
                          : "none",
                      }}
                    >
                      {showDivider ? (
                        <div className="w-full">
                          <div
                            className={classNames(
                              "flex items-center gap-2 mb-2",
                              stepIndex > 0 ? "mt-3" : undefined,
                            )}
                            style={{
                              color: currentColorPalette.secondary,
                            }}
                          >
                            <div
                              className="flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: currentColorPalette.secondary,
                                color: "#fff",
                              }}
                            >
                              {stepIndex + 1}
                            </div>
                            {stepLabel ? (
                              <span className="text-sm font-medium">
                                {stepLabel}
                              </span>
                            ) : null}
                            <div
                              className="flex-1 h-px"
                              style={{
                                backgroundColor: currentColorPalette.secondary,
                                opacity: 0.3,
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                      {renderStepSection(sectionId)}
                    </div>
                  );
                })}

                {showOverviewPage ? (
                  <VerificationOverview
                    snapshot={
                      verificationSnapshot ??
                      (draftSnapshot &&
                      Object.keys(draftSnapshot.values).length > 0
                        ? draftSnapshot
                        : null) ??
                      sessionSnapshot
                    }
                    session={session}
                    labelMap={overviewLabelMap}
                    palette={currentColorPalette}
                    rounding={currentRounding}
                    title={overviewTitle}
                    subtitle={overviewSubtitle}
                    offeringIds={bookingGroups.map((bg) => bg.offeringId)}
                  />
                ) : null}

                {/* Element ignores parent props for existing linked nodes, so we
                    control visibility via display:none on wrapper divs — same
                    pattern used for step sections above. */}
                <div
                  // Consent travels with the submit button — on a form with a
                  // review step that means the checkboxes render THERE, next to
                  // the confirm, not on the last field step.
                  style={{ display: showCheckboxes ? undefined : "none" }}
                >
                  <Element is={Checkboxes} id="checkboxes" />
                </div>

                {/* Action row — twin of the editor's action row (main-frontend
                    sections/DataInput/DataInput.tsx): Back / submit / Next on
                    ONE line, submit flexing into the rest of it, so Back reads
                    as part of the pair instead of an orphan under a full-width
                    CTA. `[&_button]:mt-0` drops the submit node's own `margin`
                    prop inside the line — the row owns the vertical spacing. */}
                <div
                  className="flex w-full items-center justify-between gap-3 mt-3 wb-md:mt-4 wb-lg:mt-5 [&_button]:mt-0"
                  style={{
                    display:
                      showSubmitButton || showBackButton || showNextButton
                        ? undefined
                        : "none",
                  }}
                >
                  <div
                    className="shrink-0"
                    style={{ display: showBackButton ? undefined : "none" }}
                  >
                    <Element
                      is={Button}
                      id="backButton"
                      buttonType="button"
                      navigationRole="back"
                    />
                  </div>
                  <div
                    className="min-w-0 flex-1"
                    style={{ display: showSubmitButton ? undefined : "none" }}
                  >
                    {/* Same seed problem for the button's `loading` /
                        `isSubmitted` (audit 2026-09-23, 4b). */}
                    <SubmitStateContext.Provider
                      value={{
                        isSubmitted: hasSubmitted || alreadySubmittedOnServer,
                        loading: state.loading,
                      }}
                    >
                      <Element
                        is={Button}
                        id="button"
                        buttonType="submit"
                        defaultText={allowUpdate ? t("update") : undefined}
                        isSubmitted={hasSubmitted || alreadySubmittedOnServer}
                        enableSubmittedState
                        loading={state.loading}
                      />
                    </SubmitStateContext.Provider>
                  </div>
                  {/* ml-auto, not just justify-between: on the FIRST step Back
                      is hidden, so Next is the row's only item and
                      justify-between parks it at the LEFT edge. */}
                  <div
                    className="ml-auto shrink-0"
                    style={{ display: showNextButton ? undefined : "none" }}
                  >
                    <Element
                      is={Button}
                      id="nextButton"
                      buttonType="button"
                      navigationRole="next"
                    />
                  </div>
                </div>

                <StatusMessage state={state} />

                {/* "Edit my submission" — belongs to the POST-submit summary
                    only. Gated on showOverviewPage it also appeared on the
                    pre-submit review step of an updatable form that the guest
                    was already re-editing, next to the submit button that does
                    the very same update. */}
                {isAllowUpdateOverview ? (
                  <button
                    type="button"
                    className="flex items-center justify-center w-full px-4 py-2 mt-2 border font-medium transition-all"
                    style={{
                      borderColor: currentColorPalette.secondary,
                      color: currentColorPalette.secondary,
                      background: "transparent",
                      borderRadius: currentRounding,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setVerificationSnapshot(null);
                      setHasSubmitted(false);
                      setIsEditing(true);
                      setState({ loading: false });
                    }}
                  >
                    {t("update")}
                  </button>
                ) : null}
              </form>
            </DataInputVerificationContext.Provider>
          </DataInputDraftContext.Provider>
        </DataInputReadOnlyContext.Provider>
      </DataInputNavigationContext.Provider>
    </ThemeSectionContainer>
  );
}
